/**
 * Composable for managing post-login redirect URLs.
 *
 * Centralizes the logic for saving and retrieving the URL that users
 * should be redirected to after authentication.
 *
 * Flow:
 * 1. On initial page load, 000-save-initial-url.client.ts saves the URL with force: true
 *    This captures the exact URL the user visited (e.g., from an email campaign link)
 * 2. During auth flow, auth middleware uses preserveQueryParams: true to avoid
 *    accidentally losing query params when Auth0 redirects strip them temporarily
 * 3. Auth0 callback URLs (with code= and state=) are never saved
 * 4. Only /lms routes are saved - other routes are ignored
 */

const STORAGE_KEY = 'post_login_redirect';
const LOGGED_IN_KEY = 'is_logged_in';
const DEFAULT_REDIRECT = '/lms/all-content';

/**
 * Check if a URL is an Auth0 callback URL
 */
function isAuth0Callback(url: string): boolean {
  return url.includes('code=') && url.includes('state=');
}

/**
 * Parse a URL string into pathname and search components.
 * Uses a dummy base URL since we only need pathname/search from relative URLs.
 */
function parseUrl(url: string): { pathname: string; search: string } {
  try {
    // Base URL is required by URL constructor for relative URLs - it's not used in output
    const parsed = new URL(url, 'http://x');
    return { pathname: parsed.pathname, search: parsed.search };
  } catch {
    return { pathname: url, search: '' };
  }
}

/**
 * Get the current stored redirect URL
 */
export function getPostLoginRedirect(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_REDIRECT;
  }
  return localStorage.getItem(STORAGE_KEY) || DEFAULT_REDIRECT;
}

/**
 * Save a redirect URL with smart conflict resolution.
 *
 * @param url - The URL to save (pathname + search, no origin)
 * @param options - Configuration options
 * @param options.force - If true, always save regardless of existing value
 * @param options.preserveQueryParams - If true, don't overwrite a URL with query params with one without (for same path)
 * @param options.source - Debug identifier for logging
 * @returns boolean - Whether the URL was saved
 */
export function savePostLoginRedirect(
  url: string,
  options: { force?: boolean; preserveQueryParams?: boolean; source?: string } = {}
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { force = false, preserveQueryParams = false, source = 'unknown' } = options;

  // Never save Auth0 callback URLs
  if (isAuth0Callback(url)) {
    return false;
  }

  // Only save LMS routes
  const { pathname, search } = parseUrl(url);
  if (!pathname.startsWith('/lms')) {
    return false;
  }

  const existingUrl = localStorage.getItem(STORAGE_KEY);

  // If forcing, just save it
  if (force) {
    localStorage.setItem(STORAGE_KEY, url);
    return true;
  }

  // If nothing stored, save it
  if (!existingUrl) {
    localStorage.setItem(STORAGE_KEY, url);
    return true;
  }

  const existing = parseUrl(existingUrl);

  // If new URL has query params, save it (query params are valuable)
  if (search) {
    localStorage.setItem(STORAGE_KEY, url);
    return true;
  }

  // If preserveQueryParams is enabled and new URL has no query params
  // but existing does for same path, don't overwrite
  if (preserveQueryParams && !search && existing.search && existing.pathname === pathname) {
    return false;
  }

  // Different path, save it
  if (existing.pathname !== pathname) {
    localStorage.setItem(STORAGE_KEY, url);
    return true;
  }

  // Same path - save if the URLs are different (handles query param changes)
  if (url !== existingUrl) {
    localStorage.setItem(STORAGE_KEY, url);
    return true;
  }

  // Exact same URL - no need to update
  return false;
}

/**
 * Clear the stored redirect URL
 */
export function clearPostLoginRedirect(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Consume the redirect URL (get and optionally clear)
 *
 * @param clear - If true, clears the stored URL after reading
 * @returns The stored URL or default
 */
export function consumePostLoginRedirect(clear = false): string {
  const url = getPostLoginRedirect();
  if (clear) {
    clearPostLoginRedirect();
  }
  return url;
}

/**
 * Mark the user as logged in via localStorage.
 * Used as an instant hint for the auth middleware to redirect
 * from `/` without waiting for Auth0 to initialize.
 */
export function setLoggedIn(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LOGGED_IN_KEY, 'true');
}

/**
 * Clear the logged-in flag from localStorage.
 * Call this before Auth0 logout to prevent stale redirects.
 */
export function clearLoggedIn(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(LOGGED_IN_KEY);
}

/**
 * Check if the user was previously logged in (localStorage hint).
 * This is NOT a source of truth — Auth0 state is authoritative.
 * The flag self-corrects: if stale, the user lands on /lms,
 * Auth0 detects unauthenticated state, and bounces to login.
 */
export function isLoggedIn(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(LOGGED_IN_KEY) === 'true';
}

/**
 * Vue composable wrapper for reactive usage
 */
export function usePostLoginRedirect() {
  return {
    get: getPostLoginRedirect,
    save: savePostLoginRedirect,
    clear: clearPostLoginRedirect,
    consume: consumePostLoginRedirect,
  };
}
